import { DEMO_CALLS, DEMO_PHONE, DEMO_PHONE_LABEL } from '@/landing/content'
import { ContentPage, contentMeta, type ContentPageData } from '@/site/content'

const sample = DEMO_CALLS.find((c) => c.key === 'prices')!

const page: ContentPageData = {
  path: '/funktsii/ai-agent-dlya-dzvinkiv',
  parent: { name: 'Можливості', path: '/funktsii' },
  crumb: 'AI-агент на дзвінках',
  title: 'AI-агент для вхідних дзвінків українською | Anytel',
  description:
    'AI-агент Anytel відповідає на вхідні дзвінки голосом українською: розмова в реальному часі, база знань, запис у календар, заявки й переведення на людину.',
  eyebrow: 'Можливості',
  h1: 'AI-агент на вхідних дзвінках',
  lead:
    'Голосовий агент бере трубку з першого гудка, розмовляє українською як людина і доводить розмову до результату: запису, заявки чи переведення на співробітника.',
  sections: [
    {
      h2: 'Як звучить розмова',
      blocks: [
        'Агент говорить у реальному часі, без пауз на кшталт «зачекайте, обробляю запит». Клієнт може перебити його, перепитати, змінити думку посеред речення, і агент підхопить, як підхопив би адміністратор. Голос, темп і манеру ви обираєте самі: від стриманої до дружньої.',
        'Під капотом працюють голосові моделі OpenAI Realtime, Gemini Live або ElevenLabs. Яку використовувати, обираєте в налаштуваннях агента: вони різняться голосами, швидкістю реакції і вартістю хвилини.',
        `Найпростіше почути це самому: подзвоніть на демо-лінію [${DEMO_PHONE_LABEL}](tel:${DEMO_PHONE}).`,
      ],
    },
    {
      h2: 'Що агент знає',
      blocks: [
        'Усе, що ви йому розкажете. У кабінеті є база знань: послуги, ціни, графік, адреси, відповіді на часті питання, правила запису. Її заповнюють звичайним текстом, без програмування. Агент відповідає за нею і не вигадує: якщо відповіді немає, скаже, що уточнить, і передасть питання людині.',
        'Якщо потрібні живі дані з ваших систем (наявність товару, статус замовлення, вільні об’єкти), агент може звертатися до них через API прямо під час розмови.',
      ],
    },
    {
      h2: 'Що агент робить',
      blocks: [
        {
          list: [
            'Записує в календар: бачить графіки співробітників і вільні вікна, подвійних записів не допускає.',
            'Приймає заявки: збирає ім’я, телефон і суть звернення, а сценарій створює заявку в кабінеті й надсилає [сповіщення в Telegram](/funktsii/zayavky-v-telegram).',
            'Переводить на людину: на співробітника чи в чергу операторів, які відповідають прямо з браузера.',
            'Запускає дії: запит у вашу систему, подію в CRM, повідомлення менеджеру. Частину дій він виконує паралельно, не перериваючи розмову.',
            'Ввічливо завершує розмову, коли питання вирішено.',
          ],
        },
      ],
    },
    {
      h2: 'Де агент стоїть у сценарії',
      blocks: [
        'Агент — один із кроків [сценарію дзвінка](/funktsii/ivr-menyu). Його можна поставити на всі дзвінки, лише на неробочий час або після голосового меню («натисніть 1, щоб записатися»). Перед ним звучить попередження про запис розмови і віртуального асистента.',
      ],
    },
  ],
  dialog: {
    h2: 'Приклад розмови',
    context: sample.context,
    dialog: sample.dialog,
    result: sample.result,
  },
  after: [
    {
      h2: 'Як перевірити агента до запуску',
      blocks: [
        {
          steps: [
            { title: 'Опишіть бізнес', text: 'Оберіть шаблон агента для своєї галузі і доповніть базу знань.' },
            { title: 'Подзвоніть з браузера', text: 'Тестовий дзвінок проходить тим самим сценарієм, що й справжні, але не потребує номера і не тарифікується.' },
            { title: 'Підключіть номер', text: 'Коли агент відповідає так, як треба, увімкніть переадресацію з робочого номера.' },
          ],
        },
      ],
    },
  ],
  faq: [
    {
      q: 'Якими мовами говорить агент?',
      a: 'Українською, англійською та іншими мовами, які підтримує обрана голосова модель. Мову, голос і вимову налаштовуєте під себе.',
    },
    {
      q: 'Чи розуміє агент суржик?',
      a: 'Агент розуміє живу розмовну мову, зокрема суржик і змішування української з російською: сучасні голосові моделі навчені на звичайному мовленні. Відповідає він тією мовою, яку ви задали. Перевірте на своїх фразах тестовим дзвінком.',
    },
    {
      q: 'Що, якщо агент не знає відповіді?',
      a: 'Він не вигадує. Скаже клієнту, що уточнить, і залежно від налаштувань переведе дзвінок на співробітника або прийме заявку на передзвін.',
    },
    {
      q: 'Скільки дзвінків агент приймає одночасно?',
      a: 'Кілька: кожен дзвінок обробляється окремо, тож у години пік клієнти не чекають на лінії. Ліміт одночасних дзвінків залежить від тарифу.',
    },
  ],
  related: ['/funktsii/ivr-menyu', '/funktsii/zayavky-v-telegram', '/funktsii/robota-24-7', '/dlya/stomatologii'],
}

export const meta = contentMeta(page)

export default function Page() {
  return <ContentPage page={page} />
}
